import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Video,
  Download,
  RefreshCw,
  AlertCircle,
  Check,
  Clock,
  Users,
  Calendar,
  FileText,
  Search,
  Mic,
  Film,
} from 'lucide-react'
import * as api from '../services/api'
import type { TeamsMeeting, TeamsTranscript } from '../types'

export default function TeamsTranscripts() {
  const { projectId } = useParams<{ projectId: string }>()
  const [meetings, setMeetings] = useState<TeamsMeeting[]>([])
  const [importedTranscripts, setImportedTranscripts] = useState<TeamsTranscript[]>([])
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [daysBack, setDaysBack] = useState(30)

  const fetchMeetings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - daysBack)

      const response = await api.getTeamsMeetings(startDate.toISOString(), undefined, 100)
      setMeetings(response.data.meetings)
    } catch (err: unknown) {
      console.error('Failed to fetch meetings:', err)
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch Teams meetings. Ensure you have the required permissions.'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [daysBack])

  const fetchImportedTranscripts = useCallback(async () => {
    if (!projectId) return
    try {
      const response = await api.getProjectTeamsTranscripts(projectId)
      setImportedTranscripts(response.data.transcripts)
    } catch (err) {
      console.error('Failed to fetch imported transcripts:', err)
    }
  }, [projectId])

  useEffect(() => {
    fetchMeetings()
    fetchImportedTranscripts()
  }, [fetchMeetings, fetchImportedTranscripts])

  const handleImport = async (meeting: TeamsMeeting) => {
    if (!projectId) return
    setImporting(meeting.id)
    setError(null)
    setSuccess(null)

    try {
      const response = await api.importTeamsTranscript(projectId, {
        meeting_id: meeting.id,
        join_url: meeting.join_url,
      })

      if (response.data.success) {
        setSuccess(`Imported transcript: ${response.data.filename}`)
        await fetchImportedTranscripts()
      } else {
        setError(response.data.message)
      }
    } catch (err: unknown) {
      console.error('Failed to import transcript:', err)
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to import transcript. Please try again.'
      setError(errorMessage)
    } finally {
      setImporting(null)
    }
  }

  const isImported = (meetingId: string) => {
    return importedTranscripts.some((t) => t.meeting_id === meetingId)
  }

  const [showOnlyWithTranscripts, setShowOnlyWithTranscripts] = useState(false)

  const filteredMeetings = meetings.filter((m) => {
    const matchesSearch =
      m.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.organizer?.toLowerCase().includes(searchTerm.toLowerCase())

    if (showOnlyWithTranscripts) {
      return matchesSearch && m.has_transcript
    }
    return matchesSearch
  })

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown'
    return new Date(dateStr).toLocaleString()
  }

  const formatDuration = (start?: string, end?: string) => {
    if (!start || !end) return ''
    const startDate = new Date(start)
    const endDate = new Date(end)
    const durationMs = endDate.getTime() - startDate.getTime()
    const minutes = Math.round(durationMs / 60000)
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}`} className="btn-ghost btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Teams Transcripts</h1>
            <p className="text-slate-400 mt-1">Import meeting transcripts from Microsoft Teams</p>
          </div>
        </div>
        <button onClick={fetchMeetings} className="btn-secondary" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="alert-success">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Imported Transcripts */}
      {importedTranscripts.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">
            Imported Transcripts ({importedTranscripts.length})
          </h2>
          <div className="space-y-2">
            {importedTranscripts.map((transcript) => (
              <div
                key={transcript.id}
                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-violet-400" />
                  <div>
                    <p className="text-slate-200">{transcript.filename}</p>
                    <p className="text-sm text-slate-500">
                      Imported {transcript.imported_at ? formatDate(transcript.imported_at) : ''}
                    </p>
                  </div>
                </div>
                <span className="badge-green">{transcript.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="label">Search Meetings</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search by subject or organizer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <label className="label">Time Range</label>
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              className="select w-full"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyWithTranscripts}
              onChange={(e) => setShowOnlyWithTranscripts(e.target.checked)}
              className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500/50"
            />
            <span className="text-sm text-slate-300">Show only meetings with transcripts</span>
          </label>
          <div className="text-sm text-slate-500">
            {meetings.filter((m) => m.has_transcript).length} of {meetings.length} have transcripts
          </div>
        </div>
      </div>

      {/* Meetings List */}
      <div className="card">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">
          Available Meetings ({filteredMeetings.length})
        </h2>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : filteredMeetings.length === 0 ? (
          <div className="text-center py-12">
            <Video className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">
              {searchTerm ? 'No meetings match your search' : 'No Teams meetings found'}
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Meetings with transcripts enabled will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredMeetings.map((meeting) => {
              const imported = isImported(meeting.id)
              const hasTranscript = meeting.has_transcript
              const hasRecording = meeting.has_recording
              const hasArtifacts = hasTranscript || hasRecording

              return (
                <div
                  key={meeting.id}
                  className={`p-4 rounded-lg border ${
                    imported
                      ? 'bg-green-500/10 border-green-500/30'
                      : hasTranscript
                        ? 'bg-slate-800/50 border-violet-500/30'
                        : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600/50'
                  } transition-colors`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          imported
                            ? 'bg-green-500/20'
                            : hasTranscript
                              ? 'bg-violet-500/20'
                              : 'bg-slate-700/50'
                        }`}
                      >
                        <Video
                          className={`w-5 h-5 ${
                            imported
                              ? 'text-green-400'
                              : hasTranscript
                                ? 'text-violet-400'
                                : 'text-slate-500'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-slate-100 truncate">
                            {meeting.subject || 'Untitled Meeting'}
                          </h3>
                          {/* Artifact badges */}
                          {hasTranscript && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                              <Mic className="w-3 h-3" />
                              Transcript
                            </span>
                          )}
                          {hasRecording && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                              <Film className="w-3 h-3" />
                              Recording
                            </span>
                          )}
                          {!hasArtifacts && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-700/50 text-slate-500 border border-slate-600/30">
                              No transcript
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(meeting.start)}
                          </span>
                          {meeting.start && meeting.end && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {formatDuration(meeting.start, meeting.end)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {meeting.attendee_count} attendees
                          </span>
                        </div>
                        {meeting.organizer && (
                          <p className="text-sm text-slate-500 mt-1">
                            Organized by {meeting.organizer}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {imported ? (
                        <span className="badge-green flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" />
                          Imported
                        </span>
                      ) : hasTranscript ? (
                        <button
                          onClick={() => handleImport(meeting)}
                          disabled={importing === meeting.id}
                          className="btn-primary btn-sm"
                        >
                          {importing === meeting.id ? (
                            <>
                              <div className="spinner w-4 h-4" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4" />
                              Import
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-500 italic">
                          No transcript available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Help Card */}
      <div className="card bg-gradient-to-r from-violet-500/10 to-blue-500/10 border-violet-500/30">
        <h3 className="font-semibold text-slate-100 mb-2">About Teams Transcripts</h3>
        <ul className="text-sm text-slate-400 space-y-1">
          <li>• Transcripts are automatically generated when enabled in Teams meetings</li>
          <li>• Only meetings where you are the organizer or attendee are shown</li>
          <li>• Imported transcripts are processed and indexed for AI chat</li>
          <li>• The AI can answer questions about meeting content after import</li>
        </ul>
      </div>
    </div>
  )
}
